<script setup lang="ts">
import Product from "~/models/Product";
import productService from "~/services/ProductService";
import {
  MAIN_BANNER_FOR_PRODUCT,
  SECONDARY_BANNER_FOR_PRODUCT,
} from "@/config/constants";
import { useWindowSize } from "@vueuse/core";

const props = defineProps({
  product: {
    type: Product,
    required: true,
  },
});

const { width } = useWindowSize();
const isMobileView = computed(() => width.value <= 576);

const getBannerImage = (banner: {
  image_url: string;
  sm_image_url?: string;
}) => {
  if (isMobileView.value && banner.sm_image_url) {
    return banner.sm_image_url;
  }
  return banner.image_url;
};

const banners = ref<{ image_url: string; sm_image_url?: string }[]>([]);

const getBannerWidth = (sectionCode: string | number) => {
  if (sectionCode == MAIN_BANNER_FOR_PRODUCT) {
    return 1920;
  } else if (sectionCode == SECONDARY_BANNER_FOR_PRODUCT) {
    return 1200;
  }
  return 1920;
};

const getBannerHeight = (sectionCode: string | number) => {
  if (sectionCode == MAIN_BANNER_FOR_PRODUCT) {
    return 200;
  } else if (sectionCode == SECONDARY_BANNER_FOR_PRODUCT) {
    return 150;
  }
  return 250;
};

const getBannerClass = (sectionCode: string | number) => {
  if (sectionCode == MAIN_BANNER_FOR_PRODUCT) {
    return "col-12 main";
  } else if (sectionCode == SECONDARY_BANNER_FOR_PRODUCT) {
    return "col-12 col-md-8 secondary";
  }
  return "col-12";
};

const fetchBanners = async () => {
  if (!props.product.id) return;
  try {
    banners.value = await productService.getBannersByProduct(props.product.id);
  } catch (e) {
    console.error("Error fetching banners by brand/category:", e);
  }
};

watch(
  () => props.product.id,
  (newId) => {
    if (newId) {
      fetchBanners();
    }
  },
  { immediate: true },
);

const imageErrors = ref<Record<string, boolean>>({});
</script>

<template>
  <div v-if="banners.length > 0" class="row">
    <div v-for="banner in banners" :key="banner.id" class="col-12">
      <a v-if="banner.action_url" :href="banner.action_url" target="_blank">
        <img
          :key="`${banner.id}-${isMobileView ? 'mobile' : 'desktop'}`"
          v-lazy="getBannerImage(banner)"
          :alt="banner.name"
          class="product-banner mt-lg-2 mt-0"
          :class="getBannerClass(banner.section_code)"
          :width="getBannerWidth(banner.section_code)"
          :height="getBannerHeight(banner.section_code)"
          @error="imageErrors[banner.id] = true"
        />
      </a>
    </div>
  </div>
</template>

<style scoped>
.product-banner {
  max-width: 100%;
  object-fit: contain;
}

.main {
  height: 200px;
}

.secondary {
  height: 150px;
}

@media (max-width: 576px) {
  .product-banner {
    width: 483px;
    height: 156px;
  }
}
</style>
